import { useCallback, useId, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ISelectOption } from "@/ui-kit";
import { ComboBox, FormField } from "@/ui-kit";
import type { ICompanyType } from "./useCompanyOptions";
import { useCompanyOptions } from "./useCompanyOptions";

type IProps = {
  name: string;
  company: ICompanyType;
  /** Called after the form value (the company id) is set. */
  onChange?: (option: ISelectOption | undefined) => void;
  disabled?: boolean;
};

/** Picks one company. The form value is the company id, `""` when empty. */
export const CompanyControl = (props: IProps) => {
  // A new company type is a new list: remount so no state of the old one survives.
  return <CompanyField key={props.company} {...props} />;
};

const CompanyField = ({ name, company, onChange, disabled }: IProps) => {
  const { t } = useTranslation();
  const {
    field: { value: companyId, onChange: onFieldChange },
    fieldState: { error },
  } = useController({ name });
  const { options, loading, loadingMore, search, loadMore } =
    useCompanyOptions(company);

  // The form keeps only the id, so the picked option keeps the name to show.
  // It shows only while it matches the form: a reset or a parent clearing the
  // id clears the field too.
  const [picked, setPicked] = useState<ISelectOption>();
  const value = picked?.id === companyId ? picked : undefined;
  // Read by `onSearch`, which must stay stable: ComboBox re-runs the search
  // with its last query whenever `onSearch` changes.
  const pickedTextRef = useRef<string | undefined>(undefined);

  const handleChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const selected = option as ISelectOption | undefined;
      setPicked(selected);
      pickedTextRef.current = selected?.text;
      onFieldChange(selected?.id ?? "");
      onChange?.(selected);
    },
    [onFieldChange, onChange],
  );

  // ComboBox puts the picked name into its search box. Searching for it would
  // narrow the list to one company, so it reopens with the full list instead.
  const onSearch = useCallback(
    (query: string) => {
      search(query === pickedTextRef.current ? "" : query);
    },
    [search],
  );

  const onClear = useCallback(() => {
    search("");
  }, [search]);

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();

  return (
    <FormField
      htmlFor={inputId}
      label={t("label.company")}
      message={error?.message}
    >
      <ComboBox
        inputId={inputId}
        options={options}
        value={value}
        onChange={handleChange}
        onClear={onClear}
        onSearch={onSearch}
        onLoad={loadMore}
        loading={loading}
        loadingMore={loadingMore}
        placeholder={t("placeholder.selectCompany")}
        invalid={!!error}
        disabled={disabled}
        maxHeight={240}
        openLength={0}
      />
    </FormField>
  );
};
